import { LoadState } from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';

const NO_SIGNAL_THRESHOLD = 15;

const SEGMENT_TYPES = {
  PICK_UP: 'PICK_UP',
  EN_ROUTE: 'EN_ROUTE',
  DROP_OFF: 'DROP_OFF',
  LOGGING: 'LOGGING',
} as const;

const loadStateSegmentTypeMap: Record<string, LoadState[]> = {
  [SEGMENT_TYPES.PICK_UP]: [LoadState.ARRIVED_PICKUP, LoadState.LOADED],
  [SEGMENT_TYPES.EN_ROUTE]: [LoadState.TO_DROPOFF, LoadState.TO_PICKUP],
  [SEGMENT_TYPES.DROP_OFF]: [LoadState.ARRIVED_DROPOFF, LoadState.UNLOADED],
};

function getType(ping: LocationHistoryMinimal): string {
  const loadState = ping?.loadState;
  for (const [type, states] of Object.entries(loadStateSegmentTypeMap)) {
    if (states.includes(loadState as LoadState)) return type;
  }
  return SEGMENT_TYPES.LOGGING;
}

interface SegmentBoundary {
  start: Dayjs;
  end: Dayjs;
}

interface Point {
  lat: number;
  lng: number;
}

interface SplitPathResult {
  solidSegments: Point[][];
  gapSegments: Point[][];
}

const SEGMENTS_PER_PAIR = 8;

function isValidCoord(pt: Point): boolean {
  return Math.abs(pt.lat) > 0.01 || Math.abs(pt.lng) > 0.01;
}

/**
 * Splits pings into solid and gap path segments. The entire path is splined
 * as one continuous curve first, then the splined points are partitioned by
 * segment boundaries. This ensures tangent continuity at solid/gap junctions.
 */
export function splitPingsBySegments(pings: LocationHistoryMinimal[]): SplitPathResult {
  if (pings.length === 0) return { solidSegments: [], gapSegments: [] };

  const boundaries = getCycleSegmentBoundaries(pings);

  // Build ordered list of valid-coord pings and tag each as solid or gap
  const validPings: { pt: Point; isSolid: boolean }[] = [];
  for (const p of pings) {
    const pt = { lat: parseFloat(p.lat), lng: parseFloat(p.lon) };
    if (!isValidCoord(pt)) continue;

    const ms = p.occurredAt.valueOf();
    const isSolid = boundaries.some(
      (b) => ms >= b.start.valueOf() && ms <= b.end.valueOf(),
    );
    validPings.push({ pt, isSolid });
  }

  if (validPings.length === 0) return { solidSegments: [], gapSegments: [] };
  if (validPings.length < 3) {
    // Too few points to spline — return raw points
    const solid = validPings.filter((v) => v.isSolid).map((v) => v.pt);
    const gap = validPings.filter((v) => !v.isSolid).map((v) => v.pt);
    return {
      solidSegments: solid.length > 0 ? [solid] : [],
      gapSegments: gap.length > 0 ? [gap] : [],
    };
  }

  // Spline the entire path as one continuous curve
  const rawPoints = validPings.map((v) => v.pt);
  const splined: { pt: Point; isSolid: boolean }[] = [];

  for (let i = 0; i < rawPoints.length - 1; i++) {
    const p0 = rawPoints[Math.max(i - 1, 0)];
    const p1 = rawPoints[i];
    const p2 = rawPoints[i + 1];
    const p3 = rawPoints[Math.min(i + 2, rawPoints.length - 1)];

    // Interpolated points between original points i and i+1 inherit the
    // solid/gap tag: solid only if BOTH endpoints are solid.
    const bothSolid = validPings[i].isSolid && validPings[i + 1].isSolid;

    for (let step = 0; step < SEGMENTS_PER_PAIR; step++) {
      const t = step / SEGMENTS_PER_PAIR;
      const t2 = t * t;
      const t3 = t2 * t;

      splined.push({
        pt: {
          lat:
            0.5 *
            (2 * p1.lat +
              (-p0.lat + p2.lat) * t +
              (2 * p0.lat - 5 * p1.lat + 4 * p2.lat - p3.lat) * t2 +
              (-p0.lat + 3 * p1.lat - 3 * p2.lat + p3.lat) * t3),
          lng:
            0.5 *
            (2 * p1.lng +
              (-p0.lng + p2.lng) * t +
              (2 * p0.lng - 5 * p1.lng + 4 * p2.lng - p3.lng) * t2 +
              (-p0.lng + 3 * p1.lng - 3 * p2.lng + p3.lng) * t3),
        },
        isSolid: bothSolid,
      });
    }
  }

  // Add the last point
  splined.push({
    pt: rawPoints[rawPoints.length - 1],
    isSolid: validPings[validPings.length - 1].isSolid,
  });

  // Group consecutive points by solid/gap, overlapping by 1 point at
  // transitions so segments connect visually.
  const solidSegments: Point[][] = [];
  const gapSegments: Point[][] = [];
  let current: Point[] = [splined[0].pt];
  let currentIsSolid = splined[0].isSolid;

  for (let i = 1; i < splined.length; i++) {
    if (splined[i].isSolid !== currentIsSolid) {
      // Include the transition point in both segments for overlap
      current.push(splined[i].pt);
      (currentIsSolid ? solidSegments : gapSegments).push(current);
      current = [splined[i].pt];
      currentIsSolid = splined[i].isSolid;
    } else {
      current.push(splined[i].pt);
    }
  }

  if (current.length > 0) {
    (currentIsSolid ? solidSegments : gapSegments).push(current);
  }

  return { solidSegments, gapSegments };
}

function getCycleSegmentBoundaries(pings: LocationHistoryMinimal[]): SegmentBoundary[] {
  const segments: LocationHistoryMinimal[][] = [[]];

  pings.forEach((p) => {
    const currentSegment = segments[segments.length - 1];
    if (!currentSegment.length) {
      currentSegment.push(p);
      return;
    }

    const lastPing = currentSegment[currentSegment.length - 1];

    if (
      getType(lastPing) !== getType(p) ||
      dayjs(p.occurredAt).diff(dayjs(lastPing.occurredAt), 'minutes') >=
        NO_SIGNAL_THRESHOLD
    ) {
      segments.push([p]);
      return;
    }

    currentSegment.push(p);
  });

  return segments
    .filter((s) => s.length > 1)
    .map((s) => ({
      start: s[0].occurredAt,
      end: s[s.length - 1].occurredAt,
    }));
}
